import React from 'react'

import {
  Box,
  CounterBadge,
  Icon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import DefaultPopOver from 'modules/core/components/EmployeeMultiFilters/_DefaultPopOver'
import type { Field } from 'modules/core/components/EmployeeMultiFilters/commonInterfaces'
import employeeStatuses, {
  EmployeeStatus,
} from 'modules/core/components/EmployeeMultiFilters/hooks/utils/employeeStatuses'
import { CONDITIONS_VALUES } from 'modules/core/components/MultiFilters/FilterItem/useFilterItem'
import FilterPanel from 'modules/core/components/MultiFilters/FilterPanel'
import type LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import type { FilterOption } from 'modules/core/components/MultiFilters/types'
import Popover from 'modules/core/components/Popover'
import type { Attachment } from 'modules/core/components/TetherComponent/types'
import { Fields as FilterFields } from 'modules/core/components/UserSelector/_filters/filterFields'
import SafeJSON from 'modules/core/lib/SafeJSON'
import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'
import i18n from 'modules/core/lib/i18n'

interface StoredFilterData {
  field: string
  value: string
  condition?: string
  custom_action?: boolean
}

export interface Props {
  label: string
  fields: readonly FilterOption[]
  filters: MultiFilterForm[]
  logicOperator: LogicOperatorForm
  /**
   * Hides the icon for a more modern look and uses only as much space as necessary
   */
  dense?: boolean
  onAddFilter: () => void
  onRemoveFilter: (index: number) => void
  onApplyFilters: (form?: MultiFilterForm[]) => void
  onCancel: () => void
  onResetFilters?: () => void
  emptyStateLabel?: string
  addFilterLabel?: string
  submitLabel?: string
  attachment?: Attachment
  filterKey?: string
  filterDependencies?: string[]
  defaultStatusFilter?: EmployeeStatus | null
}

interface State {
  advanced: boolean
  fieldSelected: string
}

interface Action {
  type: string
  field?: string
}

interface TooltipProps {
  label: string
  enabled: boolean
}

const RESET_FILTER_COUNT = 0
const DEFAULT_FILTER_COUNT = 1
const CHANGE_SELECTED_FIELD = 'change_selected_field'
const RESET_FIELDS = 'reset_fields'
const USE_ADVANCED_FILTER = 'used_advanced_filter'

const initialState = {
  advanced: false,
  fieldSelected: '',
}

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case RESET_FIELDS:
      return initialState

    case CHANGE_SELECTED_FIELD:
      return {
        ...state,
        fieldSelected: action.field || '',
      }

    case USE_ADVANCED_FILTER:
      return {
        ...state,
        advanced: true,
      }

    default:
      throw new Error(`No such type: ${action.type}`)
  }
}

const CustomTooltip = ({ label, enabled, children }: React.PropsWithChildren<TooltipProps>) => {
  const [open, toggle] = useToggle(false)

  return (
    <Tooltip.Alt attachment="top center" open={open && enabled} toggle={toggle} label={label}>
      {/* This Box prevents the tooltip from miscalculating the position when CounterBadge.count == 0 */}
      <Box>{children}</Box>
    </Tooltip.Alt>
  )
}

const DefaultFilters = ({
  label,
  emptyStateLabel,
  attachment = 'top left',
  fields,
  filters,
  logicOperator,
  dense,
  onAddFilter,
  onRemoveFilter,
  onApplyFilters,
  onCancel,
  onResetFilters,
  filterKey = LOCAL_STORAGE_KEYS.employeesFilters,
  filterDependencies = [],
  defaultStatusFilter = employeeStatuses.active,
}: Props) => {
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const { advanced, fieldSelected } = state

  const getLocalStorage = (localStorageKey: string) => {
    return SafeJSON.parse<{ default?: StoredFilterData[] } | null>(
      SafeLocalStorage.getItem(localStorageKey, null),
      null
    )
  }

  const filterValue = getLocalStorage(filterKey)
  const [open, toggle] = useToggle(false)

  const calculateCount = () => {
    if (!filterValue) return filters.length

    const filterDefaultValues = filterValue.default

    if (!filterDefaultValues?.length) {
      return filters.length
    } else if (filterDefaultValues[0]?.value === defaultStatusFilter) {
      return RESET_FILTER_COUNT
    } else {
      return DEFAULT_FILTER_COUNT
    }
  }

  const count = calculateCount()

  const resetFields = () =>
    dispatch({
      type: RESET_FIELDS,
    })

  React.useEffect(() => {
    const defaultFilter = filterValue?.default
    const defaultValues = defaultFilter?.[0]

    if (!defaultFilter?.length || !defaultValues) {
      onApplyFilters()

      return
    }

    onApplyFilters([new MultiFilterForm(defaultValues)])
  }, [...filterDependencies]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnApplyFilters = () => {
    toggle(false)()
    onApplyFilters()
    resetFields()
  }

  const handleOnCancel = () => {
    onCancel()
    resetFields()
  }

  const resetFilters = () => {
    if (onResetFilters) {
      onResetFilters()
    }
    if (!defaultStatusFilter) {
      toggle(false)()
      onApplyFilters([])
      resetFields()
    } else {
      applyFieldOption(FilterFields.EmployeeStatus, defaultStatusFilter)
    }
  }

  const changeSelectedField = (field: string) => {
    dispatch({
      type: CHANGE_SELECTED_FIELD,
      field,
    })
  }

  const applyFieldOption = async (field: string, value: string) => {
    const filterForm = new MultiFilterForm({
      condition: CONDITIONS_VALUES.isEqual,
      field,
      value,
    })
    toggle(false)()
    onApplyFilters([filterForm])
    resetFields()
  }

  const getSelectedFieldOptions = (): Field[] => {
    const field = fields.find((field) => field.value === fieldSelected)

    if (!field) return []

    const options = field.options?.map((option) => {
      return {
        title: option.label,
        selected: isOptionSelected(option.value, 'value', fieldSelected),
        onClick: () => applyFieldOption(fieldSelected, option.value),
      }
    })

    return options || []
  }

  const DEFAULT_FIELDS = [
    FilterFields.Team,
    FilterFields.Location,
    FilterFields.EmployeeStatus,
    FilterFields.JobTitle,
    FilterFields.JobRole,
  ]

  const DEFAULT_TRANSLATIONS: { [key: string]: string } = {
    team: i18n.t('filters.multi_filters.team'),
    location: i18n.t('filters.multi_filters.location'),
    job_title: i18n.t('filters.multi_filters.job_title'),
    job_role: i18n.t('filters.multi_filters.job_role'),
    employee_status: i18n.t('filters.multi_filters.employee_status'),
  }

  const getFieldOptions = () => {
    const options: Field[] = DEFAULT_FIELDS.map((field) => ({
      title: DEFAULT_TRANSLATIONS[field]!,
      selected: isOptionSelected(field),
      onClick: () => changeSelectedField(field),
    }))

    options.push({
      title: i18n.t('filters.default_filters.advanced_filter'),
      selected: isAdvancedFilterSelected(),
      onClick: () => dispatch({ type: USE_ADVANCED_FILTER }),
    })

    return options
  }

  const isAnyFilterSelected = () => {
    return count > 0
  }

  const isDefaultFilterSelected = () => {
    return isAnyFilterSelected() && (filterValue?.default?.length ?? 0) > 0
  }

  const isAdvancedFilterSelected = () => {
    return isAnyFilterSelected() && !isDefaultFilterSelected()
  }

  const getSuboptionSelected = () => {
    if (!filterValue?.default?.[0]) return undefined

    const { field, value } = filterValue.default[0]
    const selectedField = fields?.find((item) => item.value === field)

    return selectedField?.options?.find((item) => item.value === value)
  }

  const labelTooltip = () => {
    if (isDefaultFilterSelected()) {
      const value = filterValue?.default?.[0]?.field
      if (!value) return ''
      return `${DEFAULT_TRANSLATIONS[value]}: ${getSuboptionSelected()?.label}`
    }

    if (isAdvancedFilterSelected()) {
      return i18n.t('filters.default_filters.advanced_filters')
    }

    // Currently unreachable
    return ''
  }

  const isOptionSelected = (option: string, key: 'field' | 'value' = 'field', parentField = '') => {
    if (!isDefaultFilterSelected()) return false

    const selectedValue = filterValue?.default?.[0]
    if (!selectedValue) return false

    const optionMatched = option === selectedValue[key]

    if (parentField) return optionMatched && parentField === selectedValue.field

    return optionMatched
  }

  const advancedFilterFields = (fields: readonly FilterOption[]) => {
    return fields.filter((field: FilterOption) => !field.hideOnAdvanced)
  }

  const icon = open ? (dense ? 'chevron-up' : 'caret-up') : dense ? 'chevron-down' : 'caret-down'

  return (
    <Popover
      attachment={attachment}
      open={open}
      size={advanced ? 'auto' : 'bigger'}
      toggle={toggle}
      onOutsideClick={resetFields}
      offset="0 66px"
    >
      <Box
        onClick={toggle(true)}
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        background="grey000White"
        border={{
          all: {
            width: 's2',
            style: 'solid',
            color: 'grey400',
          },
        }}
        gap="s6"
        borderRadius={{
          all: 'abs008',
        }}
        cursor="pointer"
        {...(dense
          ? {
              paddingX: 's12',
              paddingY: 's6',
            }
          : {
              padding: 's8',
              width: 's140',
            })}
      >
        <Box flexDirection="row" gap="s6">
          <CustomTooltip label={labelTooltip()} enabled={isAnyFilterSelected()}>
            <CounterBadge count={count} noCountIcon={dense ? undefined : 'filter'} />
          </CustomTooltip>
          <Text size="300" color="grey1000">
            {label}
          </Text>
        </Box>
        <Icon.Small icon={icon} />
      </Box>

      {!advanced && !fieldSelected && (
        <DefaultPopOver
          header={i18n.t('filters.default_filters.filter_by')}
          reset={i18n.t('filters.default_filters.reset_filters')}
          getFields={getFieldOptions}
          onClickHeader={resetFilters}
        />
      )}

      {!!fieldSelected && (
        <DefaultPopOver
          header={DEFAULT_TRANSLATIONS[fieldSelected]!}
          onClickHeader={resetFields}
          icon="chevron-left"
          getFields={getSelectedFieldOptions}
        />
      )}

      {advanced && (
        <FilterPanel
          emptyStateLabel={emptyStateLabel}
          fields={advancedFilterFields(fields)}
          filters={filters}
          logicOperator={logicOperator}
          onAddFilter={onAddFilter}
          onRemoveFilter={onRemoveFilter}
          onApplyFilters={handleOnApplyFilters}
          onCancel={handleOnCancel}
          cancelLabel={i18n.t('filters.default_filters.return_to_list')}
        />
      )}
    </Popover>
  )
}

export default observer(DefaultFilters)
